import { api } from '@/lib/api';

export type UploadPurpose =
  | 'member_photo'
  | 'register_certificate'
  | 'minister_photo'
  | 'user_avatar'
  | 'church_logo'
  | 'church_icon'
  | 'content_artwork'
  | 'content_image'
  | 'sermon_audio'
  | 'gallery_photo'
  | 'chronicle_file'
  | 'expense_document'
  | 'member_file'
  | 'support_ticket_file'
  | 'church_document'
  | 'import_file';

interface UploadResponse {
  key: string;
  url: string | null;
  contentType: string;
  size: number;
}

/**
 * Upload a file through the API to DigitalOcean Spaces (same pattern as Weive CRM).
 *
 * The browser posts multipart to Trinity; the API PutObjects to the bucket. The
 * returned key is what resources store — never a full URL, so the bucket can move.
 */
export const uploadBlob = async (input: {
  purpose: UploadPurpose;
  blob: Blob;
  fileName?: string;
  withoutChurch?: boolean;
}): Promise<{ key: string; url: string | null; contentType: string }> => {
  const form = new FormData();
  form.append(
    'file',
    input.blob,
    input.fileName ?? `upload${extensionFor(input.blob.type)}`,
  );
  form.append('purpose', input.purpose);

  const result = await api.postForm<UploadResponse>('/storage/upload', form, {
    withoutChurch: input.withoutChurch,
  });

  return { key: result.key, url: result.url, contentType: result.contentType };
};

const extensionFor = (contentType: string): string => {
  switch (contentType) {
    case 'image/jpeg':
      return '.jpg';
    case 'image/png':
      return '.png';
    case 'image/webp':
      return '.webp';
    case 'image/heic':
      return '.heic';
    case 'image/heif':
      return '.heif';
    case 'image/svg+xml':
      return '.svg';
    default:
      return '';
  }
};
